import { DocumentNode } from '@apollo/client'
import {
  Box,
  Button,
  ProgressBar,
  Skeleton,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  PerformanceCycleFragment,
  PerformanceGoalsEditGoalsWeightsModalEmployeeFragment,
  PerformanceGoalsEditGoalsWeightsModalMetricGroupsDocument,
  PerformanceGoalsUpsertWeightsGoalsEmployeeCycleProgressDocument,
  PerformanceGoalsUpsertWeightsGoalsEmployeeCycleProgressMutationVariables,
} from 'generated/resources/graphql'
import { sumBy, times } from 'lodash'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CycleTag } from 'modules/performance_cycles/components/CycleTag'
import { buildEmployeeCycleProgressId } from 'modules/performance_goals/lib/buildEmployeeCycleProgressId'

const calculateTotalWeight = (weights: { weight: number }[]) => sumBy(weights, 'weight')

export const EditGoalsWeightsModal: React.FC<{
  employee: PerformanceGoalsEditGoalsWeightsModalEmployeeFragment
  cycle: PerformanceCycleFragment
  metricGroupsCount?: number
  refetchQueries: DocumentNode[]
  onClose: () => void
}> = ({ employee, cycle, refetchQueries, metricGroupsCount = 0, onClose }) => {
  const { data, loading } = useQuery(PerformanceGoalsEditGoalsWeightsModalMetricGroupsDocument, {
    variables: {
      employeeId: employee.id,
      cycleId: cycle.id,
    },
  })

  const [upsertWeights] = useMutation(
    PerformanceGoalsUpsertWeightsGoalsEmployeeCycleProgressDocument,
    {
      extractorFn: ({ goalsMutations }) => goalsMutations.upsertWeightsGoalsEmployeeCycleProgress,
      awaitRefetchQueries: true,
      refetchQueries,
    }
  )

  const metricGroups = useUnsafeMemo(() => data?.goals?.metricGroups ?? [], [data])
  const anyWeightedMetricGroup = metricGroups.some(({ weights }) => weights.length > 0)

  const { values, ...form } =
    useForm<PerformanceGoalsUpsertWeightsGoalsEmployeeCycleProgressMutationVariables>({
      enableReinitialize: true,
      defaultValues: {
        id: buildEmployeeCycleProgressId(employee.id, cycle.id) ?? '',
        weights: useUnsafeMemo(
          () =>
            metricGroups.map((group) => ({
              metricGroupId: group.id,
              weight: group.weights[0]?.weight ?? 0,
            })),
          [metricGroups]
        ),
      },
      validate: ({ weights }) => {
        const errors: FormErrors<PerformanceGoalsUpsertWeightsGoalsEmployeeCycleProgressMutationVariables> =
          {}

        const totalWeight = calculateTotalWeight(weights)

        if (totalWeight !== 100) {
          const remaining = 100 - totalWeight

          const remainingError = i18n.t('performance_goals.weights.error.remaining', {
            count: i18n.formatNumber(remaining) + '%',
          })
          const overError = i18n.t('performance_goals.weights.error.over', {
            count: i18n.formatNumber(Math.abs(remaining)) + '%',
          })
          errors.weights = remaining > 0 ? remainingError : overError
        }

        return errors
      },
      onSubmit: async ({ id, weights }) => {
        const result = await upsertWeights({
          variables: { id, weights },
        })
        const handled = handleResult(result, () => null)

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          onClose()
        }
        return handled
      },
    })

  const totalWeight = useUnsafeMemo(() => calculateTotalWeight(values.weights), [values.weights])

  const distributeRemainingWeight = useUnsafeCallback(() => {
    const remaining = 100 - totalWeight
    if (remaining > 0) {
      const weightsToDistribute = values.weights.filter((w) => !w.weight)
      const amountToDistribute = parseFloat((remaining / weightsToDistribute.length).toFixed(2))
      const updatedWeights = values.weights.map((w) =>
        !w.weight ? { ...w, weight: amountToDistribute } : w
      )
      form.setFieldValue('weights', updatedWeights)
    }
  }, [form, values, totalWeight])

  const canDistribute = useUnsafeMemo(
    () => totalWeight !== 100 && values.weights.some((w) => w.weight === 0),
    [totalWeight, values.weights]
  )

  const handleWeightChange = (index: number) => (value: number | null) => {
    form.setFieldValue(`weights.${index}`, {
      ...values.weights[index],
      weight: value || 0,
    })
  }

  return (
    <Modal
      title={i18n.t('performance_goals.weights.edit_goal_weights', {
        employeeName: employee.firstName,
      })}
      onClose={onClose}
      illustration="analytics-bars"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={onClose} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting || totalWeight !== 100 || loading}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s32" style={{ width: 'calc(100vw - 20vw)' }} maxWidth="sidepanelWideXL">
        <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
          <CycleTag title={cycle.title} startOn={cycle.startOn} endOn={cycle.endOn} />
          <Button
            type="upgrade"
            onClick={distributeRemainingWeight}
            disabled={!canDistribute || form.isSubmitting || loading}
          >
            {i18n.t('performance_goals.weights.distribute_remaining')}
          </Button>
        </Box>

        <Box>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeadCell colSpan={7}>
                  {i18n.t('performance_goals.weights.goal')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={2}>
                  {i18n.t('performance_goals.weights.table.current')}
                </Table.HeadCell>
                <Table.HeadCell colSpan={3}>
                  {i18n.t('performance_goals.weights.table.weight')}
                </Table.HeadCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading &&
                times(metricGroupsCount).map((i) => (
                  <Table.Row key={i}>
                    <Table.Cell colSpan={7}>
                      <Skeleton isLoading width="s200" height="s12" />
                    </Table.Cell>
                    <Table.Cell colSpan={2}>
                      <Skeleton isLoading width="s48" height="s12" />
                    </Table.Cell>
                    <Table.Cell colSpan={3}>
                      <Skeleton isLoading height="s40" />
                    </Table.Cell>
                  </Table.Row>
                ))}

              {!loading &&
                metricGroups.map(({ id, title, weights }, index) => (
                  <Table.Row key={id}>
                    <Table.Cell colSpan={7}>
                      <Text.Normal weight="medium" size="200">
                        {title}
                      </Text.Normal>
                    </Table.Cell>
                    <Table.Cell colSpan={2}>
                      <Text.Normal size="200">
                        {weights[0] || anyWeightedMetricGroup
                          ? i18n.formatNumber(weights[0]?.weight ?? 0) + '%'
                          : '-'}
                      </Text.Normal>
                    </Table.Cell>
                    <Table.Cell colSpan={3}>
                      <NumberField
                        indicator="%"
                        step={1}
                        value={values.weights[index]?.weight}
                        onChange={handleWeightChange(index)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </Box>

        <Box paddingRight="s24" alignItems="center" flexDirection="row" gap="s80">
          <Box flexGrow>
            {loading ? (
              <Skeleton isLoading height="s8" />
            ) : (
              <ProgressBar
                total={100}
                amount={totalWeight}
                progressColor={totalWeight == 100 ? 'viridian1000' : 'radicalRed1000'}
                barColor="grey400"
              />
            )}
          </Box>
          <Box style={{ width: '175px', maxWidth: '175px' }}>
            {loading ? (
              <Skeleton isLoading height="s40" />
            ) : (
              <NumberField
                indicator="%"
                error={form.errors.weights?.toString()}
                value={totalWeight}
                readonly
              />
            )}
          </Box>
        </Box>

        <Callout
          compact
          type="info"
          description={i18n.t('performance_goals.weights.modal_notice')}
        />
      </Box>
    </Modal>
  )
}
